import { CreateQuoteRequest } from 'api/accessexpressv3/schemas/QuotesSchemas';
import { useAuthUser } from 'hooks/useAuthUser';
import { CartSiteAddress, CartStore } from '../CartProvider';
import { ConfigRequirements } from '../BaseComponents/Cart/locationFlow/access/ConfigRequirementsModal/schema';
import { DraftOrderAPIResponse } from 'api/accessexpressv3/schemas/OrdersSchemas';
import { postQuote, updateOrder, postOrder } from 'api/accessexpressv3/api';

type Product = {
  product_type: 'Broadband' | 'DIA';
} & Partial<ConfigRequirements>;

export const postQuoteToRequest = (
  userInfo: ReturnType<typeof useAuthUser>,
  locations: Array<CartSiteAddress>,
  products: Product[],
): CreateQuoteRequest => {
  return {
    customer_name: userInfo.companyName,
    requester: `${userInfo.first_name} ${userInfo.last_name}`,
    business_unit: 'standard',
    email: userInfo.email ?? '',
    locations: locations.map((location) => {
      return {
        address1: location.address_line_1 ?? '',
        city: location.city ?? '',
        state: location.state ?? '',
        zip_code: location.zip ?? '',
        external_id: location.external_id ?? '',
        products: products.map((product) => {
          return {
            ...product,
            product_type: product.product_type,
            term: product?.term?.value,
            exclude_carriers: product?.exclude_carriers?.map(
              (item) => item.value,
            ),
            preferred_carriers: product?.preferred_carriers?.map(
              (item) => item.value,
            ),
            include_carriers: product?.preferred_carriers?.map(
              (item) => item.value,
            ),
            ip_type: product?.ip_type?.value,
            ip_block: product?.ip_block?.value,
            min_download: product?.download?.value,
            max_download: product?.download?.value,
          };
        }),
      };
    }),
  };
};

export const upsertDraft = async (
  userInfo: ReturnType<typeof useAuthUser>,
  cart: CartStore,
): Promise<DraftOrderAPIResponse | null> => {
  const payload = {
    additional_details: cart.additionalDetails ?? {},
    locations: cart.locations.map((location) => ({
      address: { ...location, id: location.id as string },
      order_items: cart
        .itemsByLocation(location.id)
        .filter((item) => item.category === 'Access')
        .map((item) => ({
          type: item.category ?? '',
          subtype: item.subCategory ?? '',
          pim_price_id: null,
          item_details: item.item_details ?? [],
          price: item.price ?? 0,
          quantity: item.quantity ?? 0,
          location_id: String(item.locationId ?? ''),
        })),
    })),
  };

  return cart.draftId
    ? await updateOrder(payload, cart.draftId)
    : await postOrder({
        ...payload,
        customer_name: userInfo.companyName,
        requester: `${userInfo.first_name} ${userInfo.last_name}`,
        status: 'draft',
      });
};

export const createQuote = async ({
  userInfo,
  cart,
}: {
  userInfo: ReturnType<typeof useAuthUser>;
  cart: CartStore;
}): Promise<string> => {
  const quoteResponse = await postQuote(
    postQuoteToRequest(userInfo, cart.locations, [
      {
        product_type: cart.selectedAccessProductCategory as 'Broadband' | 'DIA',
        ...cart.additionalDetails?.configs,
      },
    ]),
  );

  return quoteResponse.id;
};

export const updateDraftWithQuoteId = async ({
  cart,
  quoteId,
  draftId,
}: {
  cart: CartStore;
  quoteId: string;
  draftId: string;
}) => {
  const additionalDetails = {
    configs: cart.additionalDetails?.configs,
    ...(cart.selectedAccessProductCategory === 'DIA'
      ? { dia_quote_request: quoteId }
      : { bb_quote_request: quoteId }),
  };

  return await updateOrder({ additional_details: additionalDetails }, draftId);
};
